import { useState, useEffect, useMemo, useCallback } from 'react';
import api from './services/api';
import AssessmentTable   from './components/AssessmentTable';
import OperatorPerformanceReportPage from './components/OperatorPerformanceReportPage';
import Setup2FA          from './components/Setup2FA';
import ChangePasswordPage from './components/ChangePasswordPage';
import DepartmentsPage   from './components/DepartmentsPage';
import IndicatorsPage    from './components/IndicatorsPage';
import NewEvaluationPage from './components/NewEvaluationPage';
import EmployeesPage     from './components/EmployeesPage';
import TimeclockPage     from './components/TimeclockPage';
import TimesheetPage     from './components/TimesheetPage';
import UsersAdminPage    from './components/UsersAdminPage';
import ActiveSessionsPage from './components/ActiveSessionsPage';
import RolePermissionsPage from './components/RolePermissionsPage';
import BackupPage        from './components/BackupPage';
import HolidaysPage      from './components/HolidaysPage';
import ReportsPage       from './components/ReportsPage';
import CompaniesPage     from './components/CompaniesPage';
import AuditPage        from './components/AuditPage';
import ReplicateTemplatePage from './components/ReplicateTemplatePage';
import TrainingCoursesPage from './components/TrainingCoursesPage';
import MyTrainingPage from './components/MyTrainingPage';
import CompanyModulesPage from './components/CompanyModulesPage';
import SupportMaterialsPage from './components/SupportMaterialsPage';
import SupportMaterialsAdminPage from './components/SupportMaterialsAdminPage';
import ClinicalDashboardPage  from './components/ClinicalDashboardPage';
import ClinicalDailyEntryPage from './components/ClinicalDailyEntryPage';
import ClinicalGoalsPage      from './components/ClinicalGoalsPage';
import ClinicalExpensesPage   from './components/ClinicalExpensesPage';
import Navigation from './components/Navigation';
import Header from './components/Header';
import Footer from './components/Footer';
import Login from './components/Login';
import SignupPage from './components/SignupPage';
import PlanSelectionPage from './components/PlanSelectionPage';
import MyPlanPage from './components/MyPlanPage';
import SubscriptionsAdminPage from './components/SubscriptionsAdminPage';
import './components/styles.css';
import './components/plans.css';

// Páginas exclusivas do administrador global, independente da matriz de
// permissões: o backend já recusa (backup lê/substitui dados de todas as
// empresas; a matriz de permissões é global).
const SUPER_ADMIN_ONLY_PAGES = ['BackupPage', 'RolePermissionsPage'];

function App() {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem('user');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  // Persistido em sessionStorage (não state puro) porque o CompanySwitcher
  // troca de empresa ativa com um window.location.reload() — sem isso, todo
  // reload jogava a navegação de volta pra tela padrão (AssessmentTable),
  // perdendo onde o usuário estava (ex.: no meio de "Metas da Unidade",
  // com filtros de mês/ano preenchidos).
  const [currentComponent, setCurrentComponent] = useState(() => {
    try { return sessionStorage.getItem('currentComponent') || 'AssessmentTable'; } catch { return 'AssessmentTable'; }
  });
  // Menu lateral no celular: fica escondido e abre como gaveta pelo botão
  // de menu do Header. No desktop o CSS ignora esse estado (sidebar fixa).
  const [menuOpen, setMenuOpen] = useState(false);
  const [rolePermissions, setRolePermissions] = useState([]); // [{ role, pages }]
  const [permsLoaded, setPermsLoaded] = useState(false);
  // Módulo Treinamentos habilitado para a empresa do usuário (tela "Módulos
  // por Empresa" do administrador global) — controla o menu "Meus Treinamentos".
  const [trainingEnabled, setTrainingEnabled] = useState(false);
  // Módulo Materiais de Apoio habilitado para a empresa (mesma tela de módulos).
  const [materialsEnabled, setMaterialsEnabled] = useState(false);
  // Tela pública exibida sem login: 'login' | 'signup' (cadastro).
  const [authView, setAuthView] = useState('login');
  // Plano da empresa (GET /subscription/me): páginas liberadas, limite de
  // acessos, situação do pagamento e se precisa escolher o plano.
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    try { sessionStorage.setItem('currentComponent', currentComponent); } catch { /* ignore */ }
  }, [currentComponent]);

  const loadSubscription = useCallback(async () => {
    try {
      const { data } = await api.get('/subscription/me');
      setSubscription(data);
    } catch {
      setSubscription(null);
    }
  }, []);

  useEffect(() => {
    if (!user || user.must_change_password) return;
    loadSubscription();
  }, [user, loadSubscription]);

  const handleLogin = (userData) => {
    setUser(userData);
    setCurrentComponent('AssessmentTable');
  };

  const handleLogout = async () => {
    try {
      // Avisa o backend pra encerrar a sessão de verdade (limpa
      // active_session_id) — sem isso, clicar em "Sair" só limpava o
      // storage local: a sessão continuava "ativa" no servidor (aparecia em
      // Sessões Ativas, e bloqueava login imediato por até 30min via a
      // trava de sessão única).
      await api.post('/auth/logout');
    } catch {
      // Mesmo se falhar (rede, token já expirado etc.), ainda assim limpa o
      // lado do cliente — usuário não pode ficar preso na tela por causa
      // disso. No pior caso a sessão expira sozinha no servidor.
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('activeCompanyId');
    try { sessionStorage.removeItem('currentComponent'); } catch { /* ignore */ }
    setUser(null);
    setPermsLoaded(false);
    setSubscription(null);
    setTrainingEnabled(false);
    setMaterialsEnabled(false);
    setAuthView('login');
  };

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    api.get('/role-permissions')
      .then((r) => { if (!cancelled) setRolePermissions(r.data); })
      .catch(() => { if (!cancelled) setRolePermissions([]); })
      .finally(() => { if (!cancelled) setPermsLoaded(true); });
    return () => { cancelled = true; };
  }, [user]);

  useEffect(() => {
    if (!user || user.must_change_password) return undefined;
    let cancelled = false;
    api.get('/my-training/access')
      .then((r) => { if (!cancelled) setTrainingEnabled(r.data?.enabled === true); })
      .catch(() => { if (!cancelled) setTrainingEnabled(false); });
    api.get('/my-materials/access')
      .then((r) => { if (!cancelled) setMaterialsEnabled(r.data?.enabled === true); })
      .catch(() => { if (!cancelled) setMaterialsEnabled(false); });
    return () => { cancelled = true; };
  }, [user]);

  // Quais páginas o usuário logado pode ver: superadmin não tem restrição;
  // os demais ficam presos à matriz configurada em "Permissões" para o seu
  // type_user. Enquanto a matriz ainda não carregou, não restringe nada —
  // evita o menu piscar vazio no primeiro render.
  const allowedPages = useMemo(() => {
    if (!permsLoaded || user?.is_super_admin) return null;
    const entry = rolePermissions.find((p) => p.role === user?.type_user);
    return new Set(entry?.pages || []);
  }, [rolePermissions, permsLoaded, user]);

  // Visibilidade final = matriz de permissões do papel ∩ páginas do plano da
  // empresa (Gratuito/Plus/Master). Empresa legado (pages null) não tem
  // restrição de plano. O backend aplica as mesmas regras (planGate).
  const canSee = (pageId) => {
    if (pageId === 'MyTrainingPage' && !trainingEnabled) return false;
    if (pageId === 'SupportMaterialsPage' && !materialsEnabled) return false;
    if (SUPER_ADMIN_ONLY_PAGES.includes(pageId) && !user?.is_super_admin) return false;
    if (pageId === 'MyPlanPage') return !!subscription && !subscription.legacy && subscription.is_company_admin;
    if (!user?.is_super_admin && Array.isArray(subscription?.pages) && !subscription.pages.includes(pageId)) return false;
    return !allowedPages || allowedPages.has(pageId);
  };

  // Troca de senha obrigatória (primeiro acesso, ou senha redefinida por um
  // admin) — bloqueia TUDO, inclusive o menu, até o usuário trocar a senha.
  // Sem isolar do resto do layout, bastaria trocar currentComponent (ex. via
  // atalho/F5 numa rota antiga) pra contornar a exigência.
  const handlePasswordChanged = () => {
    const updated = { ...user, must_change_password: false };
    setUser(updated);
    try { localStorage.setItem('user', JSON.stringify(updated)); } catch { /* ignore */ }
  };

  if (!user) {
    if (authView === 'signup') {
      return <SignupPage onLogin={handleLogin} onBackToLogin={() => setAuthView('login')} />;
    }
    return <Login onLogin={handleLogin} onCreateAccount={() => setAuthView('signup')} />;
  }

  if (user.must_change_password) {
    return (
      <div className="app-layout">
        <div className="main-area">
          <Header currentComponent="ChangePasswordPage" user={user} onLogout={handleLogout} />
          <main className="content-area">
            <ChangePasswordPage forced onSuccess={handlePasswordChanged} />
          </main>
          <Footer />
        </div>
      </div>
    );
  }

  // Conta nova (cadastro público): escolher o plano é obrigatório antes de
  // liberar o sistema. O backend também bloqueia os recursos (planGate).
  if (subscription?.needs_plan_selection) {
    return (
      <div className="app-layout">
        <div className="main-area">
          <Header currentComponent="PlanSelection" user={user} onLogout={handleLogout} />
          <main className="content-area">
            <PlanSelectionPage
              subscription={subscription}
              mode="onboarding"
              onDone={() => { setCurrentComponent('AssessmentTable'); loadSubscription(); }}
            />
          </main>
          <Footer />
        </div>
      </div>
    );
  }

  const billingIssue = subscription && !subscription.legacy && subscription.is_company_admin
    && ['pending_payment', 'overdue'].includes(subscription.status) && subscription.plan_key !== 'free';

  return (
    <div className="app-layout">
      <Navigation
        onNavigationClick={(id) => { setCurrentComponent(id); setMenuOpen(false); }}
        currentComponent={currentComponent}
        user={user}
        canSee={canSee}
        isOpen={menuOpen}
        onClose={() => setMenuOpen(false)}
      />
      <div className="main-area">
        <Header
          currentComponent={currentComponent}
          user={user}
          onLogout={handleLogout}
          onMenuClick={() => setMenuOpen(true)}
        />
        <main className="content-area">
          {billingIssue && currentComponent !== 'MyPlanPage' && (
            <div className={`billing-banner ${subscription.status === 'overdue' ? 'is-danger' : ''}`}>
              <div>
                <strong>
                  {subscription.status === 'overdue' ? `Pagamento do plano ${subscription.plan_label} em atraso` : `Pagamento do plano ${subscription.plan_label} pendente`}
                </strong>
                {subscription.effective_plan_key === 'free'
                  ? 'Até a confirmação do pagamento, sua empresa usa os recursos do plano Gratuito.'
                  : `Regularize em até ${subscription.overdue_grace_days} dias para não perder os recursos do plano.`}
              </div>
              <button type="button" className="plans-btn plans-btn--dark" onClick={() => setCurrentComponent('MyPlanPage')}>Ver meu plano</button>
            </div>
          )}
          {currentComponent === 'MyPlanPage' && canSee('MyPlanPage') && <MyPlanPage subscription={subscription} onChanged={loadSubscription} />}
          {currentComponent === 'SubscriptionsAdminPage' && user?.is_super_admin && <SubscriptionsAdminPage />}
          {currentComponent === 'AssessmentTable'   && canSee('AssessmentTable')   && <AssessmentTable />}
          {currentComponent === 'OperatorPerformanceReportPage' && canSee('OperatorPerformanceReportPage') && <OperatorPerformanceReportPage />}
          {currentComponent === 'Setup2FA'          && canSee('Setup2FA')          && <Setup2FA />}
          {currentComponent === 'ChangePasswordPage' && canSee('ChangePasswordPage') && <ChangePasswordPage />}
          {currentComponent === 'DepartmentsPage'   && canSee('DepartmentsPage')   && <DepartmentsPage />}
          {currentComponent === 'IndicatorsPage'    && canSee('IndicatorsPage')    && <IndicatorsPage />}
          {currentComponent === 'NewEvaluationPage' && canSee('NewEvaluationPage') && <NewEvaluationPage />}
          {currentComponent === 'EmployeesPage'     && canSee('EmployeesPage')     && <EmployeesPage />}
          {currentComponent === 'TimeclockPage'     && canSee('TimeclockPage')     && <TimeclockPage user={user} />}
          {currentComponent === 'TimesheetPage'     && canSee('TimesheetPage')     && <TimesheetPage />}
          {currentComponent === 'UsersAdminPage'    && canSee('UsersAdminPage')    && <UsersAdminPage />}
          {currentComponent === 'ActiveSessionsPage' && canSee('ActiveSessionsPage') && <ActiveSessionsPage user={user} />}
          {currentComponent === 'RolePermissionsPage' && canSee('RolePermissionsPage') && <RolePermissionsPage />}
          {currentComponent === 'BackupPage'       && canSee('BackupPage')       && <BackupPage />}
          {currentComponent === 'HolidaysPage'    && canSee('HolidaysPage')    && <HolidaysPage />}
          {currentComponent === 'ReportsPage'     && canSee('ReportsPage')     && <ReportsPage />}
          {currentComponent === 'CompaniesPage'   && canSee('CompaniesPage')   && <CompaniesPage />}
          {currentComponent === 'AuditPage'       && canSee('AuditPage')       && <AuditPage />}
          {currentComponent === 'ReplicateTemplatePage' && user?.is_super_admin && <ReplicateTemplatePage />}
          {currentComponent === 'TrainingCoursesPage' && user?.is_super_admin && <TrainingCoursesPage />}
          {currentComponent === 'CompanyModulesPage' && user?.is_super_admin && <CompanyModulesPage />}
          {currentComponent === 'MyTrainingPage' && canSee('MyTrainingPage') && <MyTrainingPage />}
          {currentComponent === 'SupportMaterialsPage' && canSee('SupportMaterialsPage') && <SupportMaterialsPage />}
          {currentComponent === 'SupportMaterialsAdminPage' && user?.is_super_admin && <SupportMaterialsAdminPage />}
          {currentComponent === 'ClinicalDashboardPage'  && canSee('ClinicalDashboardPage')  && <ClinicalDashboardPage />}
          {currentComponent === 'ClinicalDailyEntryPage' && canSee('ClinicalDailyEntryPage') && <ClinicalDailyEntryPage />}
          {currentComponent === 'ClinicalGoalsPage'      && canSee('ClinicalGoalsPage')      && <ClinicalGoalsPage />}
          {currentComponent === 'ClinicalExpensesPage'   && canSee('ClinicalExpensesPage')   && <ClinicalExpensesPage />}
        </main>
        <Footer />
      </div>
    </div>
  );
}

export default App;
